import { useEffect, useRef, type RefObject } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { NAV_SECTIONS } from "../../shared/config/site";
import { LANGS, langFromPath, localizedPath } from "../../shared/i18n/lang";
import { openMerchantStore } from "../../shared/lib/storeLink";
import { Btn, Icon, SectionLink, StoreBtn } from "../../shared/ui";
import "./menu.css";

interface MenuSheetProps {
  open: boolean;
  onClose: () => void;
  /** Element that receives focus when the sheet closes (the header menu button). */
  returnFocusRef: RefObject<HTMLElement | null>;
}

// Mobile bottom-sheet menu. Rendered as a SIBLING of <header> (never a child): a position:fixed
// overlay inside a header with backdrop-filter / transform would be clipped to the header (0012).
export function MenuSheet({ open, onClose, returnFocusRef }: MenuSheetProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const lang = langFromPath(pathname);
  const sheetRef = useRef<HTMLDivElement>(null);
  const wasOpen = useRef(false);
  // Latest onClose without re-running the effects below on every parent render.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // Body scroll lock + focus management.
  useEffect(() => {
    document.body.classList.toggle("locked", open);
    if (open) {
      sheetRef.current?.focus({ preventScroll: true });
    } else if (wasOpen.current) {
      returnFocusRef.current?.focus({ preventScroll: true });
    }
    wasOpen.current = open;
    return () => document.body.classList.remove("locked");
  }, [open, returnFocusRef]);

  // Escape closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Route change closes.
  useEffect(() => {
    onCloseRef.current();
  }, [pathname]);

  return (
    <div className={`m-wrap${open ? " open" : ""}`} id="menu" aria-hidden={!open}>
      <div className="m-scrim" onClick={onClose} />
      <div
        className="m-sheet"
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={t("menu.title")}
        tabIndex={-1}
      >
        <div className="handle" />
        <div className="m-head">
          <b>{t("menu.title")}</b>
          <button type="button" className="m-x" onClick={onClose} aria-label={t("menu.close")}>
            <Icon name="i-x" />
          </button>
        </div>
        {NAV_SECTIONS.map((s) => (
          <SectionLink key={s.id} id={s.id} className="m-row" onNavigate={onClose}>
            {t(s.key)}
            <Icon name="i-chevR" />
          </SectionLink>
        ))}
        <div className="m-lang" aria-label={t("menu.title")}>
          {LANGS.map((lng) => (
            <Link
              key={lng}
              to={localizedPath(pathname, lng)}
              className={lang === lng ? "on" : undefined}
              aria-current={lang === lng ? "true" : undefined}
              onClick={onClose}
            >
              {t(`menu.lang_${lng}`)}
            </Link>
          ))}
        </div>
        <Btn
          as="a"
          variant="primary"
          className="m-row-cta"
          href="#download"
          onClick={(e) => {
            e.preventDefault();
            onClose();
            openMerchantStore();
          }}
        >
          {t("nav.cta")}
        </Btn>
        <div className="m-stores">
          <StoreBtn store="apple" tone="secondary" />
          <StoreBtn store="play" tone="secondary" />
        </div>
      </div>
    </div>
  );
}
